import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tabs } from 'antd';
import Layout from 'components/Layout';
import { getMerchantById } from 'api/merchants';
import { getData } from 'utils';
import { captureException } from 'utils/errors';
import { PERMISSIONS } from 'constants/permissions';
import withGuard from 'components/withGuard';
import AboutTab from './AboutTab';
import CatalogTab from './CatalogTab';
import MenusTab from './MenusTab';
import AppsTab from './AppsTab';
import SettingsTab from './SettingsTab';
import { StyledWrapper } from './styles';

/** Order: About, Apps, Settings, Catalog, Menus */
export const POSHUB_TAB_KEYS = ['about', 'apps', 'settings', 'catalog', 'menus'];

function PosHubConsole() {
  const navigate = useNavigate();
  const { id: restaurantId, tab: tabFromPath } = useParams();
  const [merchant, setMerchant] = useState(null);
  const [loading, setLoading] = useState(true);
  const redirectedRef = useRef(false);

  const posConsoleBasePath = useMemo(
    () => `/console/restaurants/${restaurantId}/pos`,
    [restaurantId],
  );

  const activeTab = POSHUB_TAB_KEYS.includes(tabFromPath) ? tabFromPath : 'about';

  useEffect(() => {
    if (!restaurantId) return;
    const needsNormalize = !tabFromPath || !POSHUB_TAB_KEYS.includes(tabFromPath);
    if (needsNormalize) {
      navigate(`${posConsoleBasePath}/${activeTab}`, { replace: true });
    }
  }, [restaurantId, tabFromPath, activeTab, navigate, posConsoleBasePath]);

  const goToTab = useCallback(
    (key) => {
      if (!restaurantId || !POSHUB_TAB_KEYS.includes(key)) return;
      navigate(`${posConsoleBasePath}/${key}`);
    },
    [navigate, posConsoleBasePath, restaurantId],
  );

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    getMerchantById(restaurantId)
      .then((res) => getData(res))
      .then(setMerchant)
      .catch(captureException)
      .finally(() => setLoading(false));
  }, [restaurantId]);

  useEffect(() => {
    if (loading || !restaurantId || redirectedRef.current) return;
    if (merchant && merchant.pos_integration_ui_enabled === false) {
      redirectedRef.current = true;
      navigate(`/console/restaurants/${restaurantId}`, { replace: true });
    }
  }, [loading, merchant, navigate, restaurantId]);

  const tabItems = [
    {
      key: 'about',
      label: 'About',
      children: (
        <AboutTab
          restaurantId={restaurantId}
          merchant={merchant}
          onGoToTab={goToTab}
          activeTab={activeTab}
        />
      ),
    },
    {
      key: 'apps',
      label: 'Apps',
      children: <AppsTab restaurantId={restaurantId} />,
    },
    {
      key: 'settings',
      label: 'Settings',
      children: <SettingsTab restaurantId={restaurantId} />,
    },
    {
      key: 'catalog',
      label: 'Catalog',
      children: <CatalogTab restaurantId={restaurantId} />,
    },
    {
      key: 'menus',
      label: 'Menus',
      children: <MenusTab restaurantId={restaurantId} />,
    },
  ];

  return (
    <Layout
      header={(
        <Layout.Header
          title={merchant?.name ?? 'POS integration'}
          loading={loading}
          backTitle="Merchant info"
          backPath={`/console/restaurants/${restaurantId}`}
        />
      )}
    >
      <StyledWrapper>
        <Tabs
          className="poshub-console-tabs"
          activeKey={activeTab}
          onChange={goToTab}
          items={tabItems}
        />
      </StyledWrapper>
    </Layout>
  );
}

export default withGuard(PosHubConsole, [PERMISSIONS.MERCHANTS], { redirect: true });
